import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchAuthConfig,
  login as apiLogin,
  getStoredToken,
  setStoredToken,
  onUnauthorized,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = unknown (still loading); object = decided
  const [config, setConfig] = useState(null); // { auth_required: bool }
  const [token, setToken] = useState(() => getStoredToken());
  const [username, setUsername] = useState(null);

  // 1. On mount, ask the backend whether auth is required.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await fetchAuthConfig();
        if (alive) setConfig(cfg);
      } catch {
        // If we can't even reach /auth/config, assume auth is not required
        // so the app at least tries to render. The real API calls will
        // surface the actual error.
        if (alive) setConfig({ auth_required: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  // 2. Globally react to any 401 by clearing the token in state.
  useEffect(() => {
    const off = onUnauthorized(() => {
      setToken(null);
      setUsername(null);
    });
    return off;
  }, []);

  const signIn = useCallback(async ({ username: u, password }) => {
    const res = await apiLogin({ username: u, password });
    if (res.auth_disabled) {
      // Server has auth off; just unlock the UI.
      setToken("");
      setUsername(u);
      return res;
    }
    setStoredToken(res.token);
    setToken(res.token);
    setUsername(res.username || u);
    return res;
  }, []);

  const signOut = useCallback(() => {
    setStoredToken(null);
    setToken(null);
    setUsername(null);
  }, []);

  // Decision: is the user allowed in right now?
  const ready = config !== null;
  const authRequired = !!config?.auth_required;
  const isAuthenticated = !authRequired || !!token;

  const value = useMemo(
    () => ({
      ready,
      authRequired,
      isAuthenticated,
      token,
      username,
      signIn,
      signOut,
    }),
    [ready, authRequired, isAuthenticated, token, username, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
